import { useQueryClient } from "@tanstack/react-query";
import { Check, Plus, Tags } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type * as S from "@releo/shared";
import { TAG_COLORS } from "@releo/shared/constants";
import { http } from "@/lib/api";
import { keys, useTags } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { toastError } from "./common";
import { Button, Dialog, Field, Input, Spinner } from "./ui";

const HEX = /^#[0-9a-fA-F]{6}$/;

/** Black or white, whichever reads best on the given background. */
function textOn(hex: string): string {
  if (!HEX.test(hex)) return "#fff";
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
  return luminance > 0.4 ? "#0f172a" : "#fff";
}

export function TagBadge({ tag, className, children }: { tag: Pick<S.Tag, "name" | "color">; className?: string; children?: ReactNode }) {
  return (
    <span
      className={cn("inline-flex max-w-full items-center gap-1 truncate rounded-full px-2 py-0.5 text-xs font-medium", className)}
      style={{ backgroundColor: tag.color, color: textOn(tag.color) }}
    >
      {tag.name}
      {children}
    </span>
  );
}

export function TagList({ tags, className }: { tags: S.Tag[]; className?: string }) {
  if (!tags.length) return null;
  return (
    <span className={cn("flex flex-wrap gap-1", className)}>
      {tags.map((tag) => (
        <TagBadge key={tag.id} tag={tag} />
      ))}
    </span>
  );
}

/** Preset swatches, native color picker and hex field. */
export function ColorInput({ value, onChange, id }: { value: string; onChange: (hex: string) => void; id?: string }) {
  const { t } = useTranslation();
  const [text, setText] = useState(value);
  const set = (hex: string) => {
    setText(hex);
    onChange(hex);
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {TAG_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => set(c)}
            className={cn("flex h-6 w-6 items-center justify-center rounded-full ring-offset-2 ring-offset-panel", value.toLowerCase() === c && "ring-2 ring-brand-500")}
            style={{ backgroundColor: c, color: textOn(c) }}
            aria-label={c}
          >
            {value.toLowerCase() === c && <Check className="h-3.5 w-3.5" />}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={HEX.test(value) ? value : "#000000"}
          onChange={(e) => set(e.target.value)}
          className="h-9 w-10 shrink-0 cursor-pointer rounded-md border bg-panel p-1"
          aria-label={t("tags.color")}
        />
        <Input
          id={id}
          value={text}
          maxLength={7}
          onChange={(e) => {
            const v = e.target.value.startsWith("#") ? e.target.value : `#${e.target.value}`;
            setText(v);
            if (HEX.test(v)) onChange(v.toLowerCase());
          }}
          className={cn("w-28 font-mono", !HEX.test(text) && "outline-2 outline-red-500")}
        />
      </div>
    </div>
  );
}

/** Tag name + color form, shared by the app tag picker and the admin page. */
export function TagFields({ value, onChange, idPrefix = "tag" }: { value: { name: string; color: string }; onChange: (v: { name: string; color: string }) => void; idPrefix?: string }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <Field label={t("common.name")} htmlFor={`${idPrefix}-name`}>
        <Input id={`${idPrefix}-name`} value={value.name} maxLength={32} onChange={(e) => onChange({ ...value, name: e.target.value })} placeholder={t("tags.namePlaceholder")} />
      </Field>
      <Field label={t("tags.color")} htmlFor={`${idPrefix}-color`}>
        <ColorInput id={`${idPrefix}-color`} value={value.color} onChange={(color) => onChange({ ...value, color })} />
      </Field>
      {value.name.trim() && (
        <div className="text-xs text-fg-muted">
          {t("tags.preview")} <TagBadge tag={{ name: value.name.trim(), color: value.color }} />
        </div>
      )}
    </div>
  );
}

/** Dialog to pick the tags of an app, and create new ones. */
export function AppTagsDialog({ app, open, onOpenChange }: { app: S.App; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const tags = useTags();
  const [selected, setSelected] = useState<Set<string>>(() => new Set(app.tags.map((tag) => tag.id)));
  const [draft, setDraft] = useState<{ name: string; color: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const createTag = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const tag = await http.post<S.Tag>("/tags", { name: draft.name.trim(), color: draft.color });
      await qc.invalidateQueries({ queryKey: keys.tags });
      setSelected((s) => new Set(s).add(tag.id));
      setDraft(null);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await http.put(`/apps/${app.id}/tags`, { tagIds: [...selected] });
      void qc.invalidateQueries({ queryKey: keys.apps });
      toast.success(t("common.saved"));
      onOpenChange(false);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) setSelected(new Set(app.tags.map((tag) => tag.id)));
        setDraft(null);
        onOpenChange(o);
      }}
      title={t("tags.editTitle", { name: app.name })}
      description={t("tags.editHelp")}
      footer={
        draft ? (
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              {t("common.cancel")}
            </Button>
            <Button loading={busy} disabled={!draft.name.trim() || !HEX.test(draft.color)} onClick={createTag}>
              {t("tags.create")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button loading={busy} onClick={save}>
              {t("common.save")}
            </Button>
          </>
        )
      }
    >
      {draft ? (
        <TagFields value={draft} onChange={setDraft} idPrefix="new-tag" />
      ) : tags.isLoading ? (
        <Spinner />
      ) : (
        <div className="space-y-3">
          {tags.data?.length ? (
            <div className="flex flex-wrap gap-2">
              {tags.data.map((tag) => {
                const on = selected.has(tag.id);
                return (
                  <button key={tag.id} type="button" onClick={() => toggle(tag.id)} aria-pressed={on} className={cn("rounded-full transition-opacity", !on && "opacity-40 hover:opacity-70")}>
                    <TagBadge tag={tag} className="px-2.5 py-1">
                      {on && <Check className="h-3 w-3" />}
                    </TagBadge>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-fg-muted">{t("tags.noneYet")}</p>
          )}
          <Button variant="outline" size="sm" onClick={() => setDraft({ name: "", color: TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)]! })}>
            <Plus className="h-3.5 w-3.5" />
            {t("tags.new")}
          </Button>
        </div>
      )}
    </Dialog>
  );
}

export function EditTagsButton({ app }: { app: S.App }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Tags className="h-4 w-4" />
        {t("tags.edit")}
      </Button>
      <AppTagsDialog app={app} open={open} onOpenChange={setOpen} />
    </>
  );
}
